import { addState } from 'ad-hok'
import { addHandlers } from 'ad-hok'
import { addDisplayName, addProps, flowMax } from 'ad-hok'
import React, { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi, addUser } from 'shared/src/api'
import { isImageFeedContent, videoThumbnail } from 'shared/src/api/types'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { formattedStat } from 'shared/src/utils/formatting'
import { typedAs } from 'shared/src/utils/typedAs'
import { subWeeks, subMonths } from 'date-fns'
import { addOptionalIntPathParam } from 'shared/src/utils/addNavigate'

import { addCmsLoadingAndErrorIndicators } from 'utils/addCmsLoadingAndErrorIndicators'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Page } from '../Page'
import {
  MAIN_TITLE_STYLE,
  SANS_SERIF_FONT_FAMILY,
  SERIF_FONT_FAMILY,
} from '../styles'
import backgroundImage from '../../assets/down-chevron.svg'
import viewsIcon from '../../assets/secondary-views-icon.svg'

const useStyles = createUseStyles({
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
    marginBottom: 3,
  },
  title: MAIN_TITLE_STYLE,
  select: {
    width: 144,
    height: 48,
    background: 'rgba(203, 166, 122, 0.08)',
    border: 'none',
    borderRadius: 2,
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 16,
    color: 'rgba(54, 52, 50, 0.8)',
    backgroundImage: `url(${backgroundImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '115px 16px',
  },
  headerStatsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: 125,
    paddingTop: 36,
  },
  headerStat: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  statAmount: {
    fontFamily: SERIF_FONT_FAMILY,
    color: '#232323',
    fontWeight: 700,
    fontSize: 36,
    lineHeight: '140%',
    letterSpacing: 0.4,
  },
  statTitle: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    color: '#CBA67A',
    fontWeight: 500,
    fontSize: 12,
    lineHeight: '160%',
    opacity: 0.8,
    letterSpacing: 0.2,
  },
  subheaderStatsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: 18,
  },
  subheaderStat: {
    alignItems: 'center',
    display: 'flex',
  },
  subheaderStatAmount: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    color: '#363432',
    fontWeight: 800,
    fontSize: 14,
    lineHeight: '160%',
    letterSpacing: 0.2,
  },
  subheaderStatTitle: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    color: 'rgba(54, 52, 50, 0.56)',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '160%',
    letterSpacing: 0.2,
    paddingLeft: 3,
  },
  galleryContainer: {
    paddingTop: 32,
  },
  galleryRow: {
    paddingBottom: 47,
  },
  galleryTitle: {
    color: '#454545',
    fontFamily: SERIF_FONT_FAMILY,
    fontWeight: 600,
    fontSize: 18,
    lineHeight: '140%',
    paddingBottom: 16,
  },
  galleryImageRow: {
    display: 'flex',
    overflowX: 'scroll',
  },
  galleryImageContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 24,
    marginRight: 24,
  },
  galleryImage: {
    minWidth: 120,
    height: 212,
    objectFit: 'cover',
    width: '100%',
    background: '#B1A186',
  },
  galleryViewsContainer: {
    display: 'flex',
  },
  galleryViews: {
    color: 'rgba(144, 115, 79, 0.8)',
    marginLeft: 6,
  },
})

type DateRange = 'allTime' | 'thisMonth' | 'thisWeek'

const getStartDate = (dateRange: DateRange): Date | undefined => {
  let date = new Date()
  if (dateRange === 'thisMonth') {
    date = subMonths(date, 1)
  } else if (dateRange === 'thisWeek') {
    date = subWeeks(date, 1)
  }

  return dateRange === 'allTime' ? undefined : date
}

interface SubheaderStatProps {
  value: number
  label: string
  showPlusSign: boolean
}

const SubheaderStat: FC<SubheaderStatProps> = flowMax(
  addDisplayName('subheaderStat'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ value, label, showPlusSign, classes }) => (
    <div className={classes.subheaderStat}>
      <div className={classes.subheaderStatAmount}>
        {showPlusSign && value > 0 && '+'}
        {formattedStat(value)}
      </div>
      <div className={classes.subheaderStatTitle}>{label}</div>
    </div>
  )
)

interface HeaderStatProps {
  value: number
  label: string
}

const HeaderStat: FC<HeaderStatProps> = flowMax(
  addDisplayName('headerStat'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ label, value, classes }) => (
    <div className={classes.headerStat}>
      <div className={classes.statAmount}>{formattedStat(value)}</div>
      <div className={classes.statTitle}>{label}</div>
    </div>
  )
)

export const Insights: FC = flowMax(
  addDisplayName('insights'),
  addState('dateRange', 'setDateRange', typedAs<DateRange>('allTime')),
  addApi,
  addUser,
  addOptionalIntPathParam('userId'),
  addDataLoadingFromPromise(
    'insights',
    ({ api, user, userId, dateRange }) =>
      api.insights.get(userId ?? user.id, getStartDate(dateRange)),
    ['api', 'dateRange'],
    ({ dateRange }) => `insights-${dateRange}`
  ),
  addCmsLoadingAndErrorIndicators('insights'),
  addHandlers({
    onDateRangeChange:
      ({ setDateRange }) =>
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        setDateRange(event.target.value as DateRange)
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({ classes, dateRange, onDateRangeChange, insights, t }) => (
    <Page>
      <div className={classes.headerContainer}>
        <div className={classes.title}>{t('admin.insights.title')}</div>
        <select
          value={dateRange}
          onChange={onDateRangeChange}
          className={classes.select}
        >
          <option value="allTime">{t('admin.insights.allTime')}</option>
          <option value="thisMonth">{t('admin.insights.thisMonth')}</option>
          <option value="thisWeek">{t('admin.insights.thisWeek')}</option>
        </select>
      </div>
      <div className={classes.headerStatsContainer}>
        <HeaderStat
          value={insights.stats.mediaViews}
          label={t('admin.insights.rodeoViews')}
        />
        <HeaderStat
          value={insights.stats.behindTheGateViews}
          label={t('admin.insights.editViews')}
        />
      </div>
      <div className={classes.subheaderStatsContainer}>
        <SubheaderStat
          value={insights.stats.totalFollowers}
          label={t('admin.insights.followers')}
          showPlusSign={dateRange !== 'allTime'}
        />
        <SubheaderStat
          value={insights.stats.totalMedia}
          label={t('admin.insights.posts')}
          showPlusSign={dateRange !== 'allTime'}
        />
        <SubheaderStat
          value={insights.stats.totalEdits}
          label={t('admin.insights.edits')}
          showPlusSign={dateRange !== 'allTime'}
        />
      </div>
      <div className={classes.galleryContainer}>
        <div className={classes.galleryRow}>
          <div className={classes.galleryTitle}>
            {t('admin.insights.topPosts')}
          </div>
          <div className={classes.galleryImageRow}>
            {insights.topMedia.map((media) => (
              <div className={classes.galleryImageContainer} key={media.id}>
                <img
                  src={
                    isImageFeedContent(media.content)
                      ? media.content.value.full
                      : videoThumbnail(media.content)
                  }
                  className={classes.galleryImage}
                  alt=""
                />
                <div className={classes.galleryViewsContainer}>
                  <img src={viewsIcon} alt="" />
                  <div className={classes.galleryViews}>{media.numViews}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={classes.galleryRow}>
          <div className={classes.galleryTitle}>
            {t('admin.insights.topProducts')}
          </div>
          <div className={classes.galleryImageRow}>
            {insights.topProducts.map((product) => (
              <div className={classes.galleryImageContainer} key={product.id}>
                <img
                  src={product.image.thumbnail}
                  className={classes.galleryImage}
                  alt={product.name}
                />
                <div className={classes.galleryViewsContainer}>
                  <img src={viewsIcon} alt="" />
                  <div className={classes.galleryViews}>{product.views}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Page>
  )
)
